/**
 * Les donjons (GDD §8). Module pur.
 *
 * Une descente est une **course, pas une boucle** : les salles s'enchaînent,
 * les PV ne se régénèrent que partiellement entre elles, et échouer perd la
 * descente *et* la clé.
 *
 * Le farm teste la soutenabilité : est-ce que je régénère plus vite que
 * j'encaisse ? Le donjon teste l'endurance totale : est-ce que mon budget de PV
 * tient sur cinq combats d'affilée ? Même statistiques, question différente :
 * un équipement confortable en farm peut très bien ne pas passer le donjon.
 */
import type { Personnage, EnDescente } from "./etat.ts"
import type { Contenu, Palier } from "./contenu.ts"
import { monstre, famille, item } from "./contenu.ts"
import { type StatsPerso, palierDe, tempsParKill, degatsSubisPs, xpGagnee } from "./combat.ts"

export interface Donjon {
  id: number
  nom: string
  salles: number
  boss: number[]
  cles: { item: number; qte: number }[]
  niveauMin: number
  niveauOptimal: number
  zone?: number | null
}

/**
 * Ce que le donjon est devenu.
 *
 * Il se jouait d'un clic : la descente entière était résolue et on lisait un
 * compte rendu. On ne voyait donc jamais ce qu'on était venu voir. Une descente
 * est maintenant une **activité**, au même titre que le farm : chaque salle
 * aligne ses gardes, on les affronte l'un après l'autre, et la salle du boss
 * oppose un adversaire à la vie démesurée dont la barre descend pour de bon.
 *
 * Il n'y a plus de repos gratuit entre les salles : plus rien ne se régénère
 * dans ce jeu (GDD §4), et un donjon se tient à coups de vivres. C'est ce qui
 * en fait une épreuve d'endurance — le sac autant que les statistiques.
 */
/** Ce que le boss multiplie sur les taux de chute affichés. */
export const BOSS_TAUX = 3
export const MONSTRES_PAR_SALLE = 3
/** Vie du boss, en multiples de celle d'un monstre ordinaire de son palier. */
export const BOSS_PV = 8
/** Le boss frappe plus fort qu'un garde, sans quoi sa vie seule ferait la difficulté. */
export const BOSS_DEGATS = 1.4

/** Compte rendu remis à la sortie, pour l'écran de fin. */
export interface Descente {
  donjon: number
  reussi: boolean
  /** salle atteinte, celle où l'on est tombé en cas d'échec */
  salle: number
  salles: number
  vaincus: number
  butin: Record<number, number>
  /** ce que la descente a coûté et rapporté */
  xp: number
  dureeS: number
  vivres: number
  /** vrai la première fois que ce boss tombe : il rejoint la collection */
  premiere: boolean
  /** niveaux gagnés pendant la descente */
  niveaux: number
  /**
   * Nombre de descentes que ce bilan résume.
   *
   * Vaut 1 pour une descente isolée. Au-delà, le joueur a lancé une série
   * (« si on a 50 clés qu'on puisse lancer 50 runs », Vyshu, 26/08/2026) et les
   * chiffres sont cumulés : butin, XP, durée, vivres, victoires. `reussi` dit
   * alors si la **série** est allée à son terme.
   */
  runs?: number
  /** descentes gagnées dans la série */
  gagnees?: number
}

/**
 * Les gardes des salles intermédiaires.
 *
 * Ils étaient tirés de la **zone** du donjon, autour de son niveau : le Château
 * Ensablé, planté en Astrub, se peuplait donc de Bouftous. La bonne clé est la
 * **famille du boss** — les monstres portent ce lien dans les données, et le
 * boss du Château Ensablé appartient à celle des Pichons. Cent vingt et un
 * donjons sur cent vingt-six y trouvent leurs gardes ; les autres retombent sur
 * la zone, faute de mieux.
 */
function gardes(d: Donjon, c: Contenu): number[] {
  const boss = d.boss[0] !== undefined ? monstre(c, d.boss[0]) : null
  if (boss?.famille != null) {
    const freres = Object.values(c.monstres)
      .filter((m) => m.famille === boss.famille && m.id !== boss.id)
      .map((m) => m.id)
    if (freres.length > 0) return freres
  }

  // Rattrapage par le nom : « Donjon des Bworks » n'a pas de famille côté boss,
  // mais « Bworks de Cania » existe. On rapproche sur le mot le plus long du
  // nom du donjon, celui qui le distingue.
  const mots = d.nom.toLowerCase().split(/[^a-zà-ÿ]+/).filter((m) => m.length >= 5)
  const parNom = Object.values(c.familles).filter((f) => {
    const n = f.nom.toLowerCase()
    return mots.some((m) => n.includes(m) || m.includes(n.split(/[^a-zà-ÿ]+/)[0] ?? "\u0000"))
  })
  if (parNom.length > 0) {
    const proches = parNom
      .sort((a, b) => Math.abs(a.niveauMax - (d.niveauMin || 0)) - Math.abs(b.niveauMax - (d.niveauMin || 0)))
    const ids = proches[0]!.monstres
    if (ids.length > 0) return ids
  }

  const cible = d.niveauOptimal || d.niveauMin
  const candidats = Object.values(c.familles)
    .filter((f) => d.zone == null || f.zone === d.zone)
    .flatMap((f) => f.monstres)
    .map((id) => monstre(c, id))
    .filter((m): m is NonNullable<typeof m> => Boolean(m))
    .sort((a, b) =>
      Math.abs(palierDe(a).niveau - cible) - Math.abs(palierDe(b).niveau - cible))
  if (candidats.length > 0) return candidats.slice(0, 8).map((m) => m.id)
  // Zone sans famille rattachée : le boss tient lieu de garde.
  return d.boss
}

export function possedeCles(d: Donjon, inventaire: Record<number, number>): boolean {
  return d.cles.every((k) => (inventaire[k.item] ?? 0) >= k.qte)
}

/**
 * Résout une descente. Déterministe à graine donnée, comme tout le moteur.
 */
/**
 * Les monstres d'une salle. La dernière n'aligne que le boss.
 *
 * Les gardes sont tirés de la zone du donjon, autour de son niveau optimal :
 * une descente ressemble ainsi à la zone qui l'entoure.
 */
export function composerSalle(d: Donjon, c: Contenu, salle: number, rng: () => number): number[] {
  if (salle >= d.salles) return [d.boss[0] ?? gardes(d, c)[0]!].filter((x) => x !== undefined)
  const g = gardes(d, c)
  return Array.from({ length: MONSTRES_PAR_SALLE }, () => g[Math.floor(rng() * g.length)] ?? g[0]!)
}

/** Vie de départ d'un adversaire de donjon : le boss en a beaucoup. */
export const pvDeDepart = (pal: Palier, boss: boolean) => pal.pv * (boss ? BOSS_PV : 1)

/**
 * Intervalle visé entre deux frappes. Le même que celui de l'animation du farm,
 * pour que les deux écrans battent au même rythme.
 */
export const INTERVALLE_COUP_MS = 850

/**
 * Découpe un combat en coups.
 *
 * Le temps total ne bouge pas : on répartit la vie du monstre sur le nombre de
 * frappes qu'il faut pour l'abattre, et l'intervalle s'ajuste pour retomber
 * exactement sur la durée voulue. C'est un habillage du même équilibrage.
 */
export function decouper(s: StatsPerso, pal: Palier, boss: boolean): { degatCoup: number; cadenceMs: number } {
  const pv = pvDeDepart(pal, boss)
  const duree = tempsParKill(s, pal) * (boss ? BOSS_PV : 1)
  const coups = Math.max(1, Math.round((duree * 1000) / INTERVALLE_COUP_MS))
  return { degatCoup: pv / coups, cadenceMs: (duree * 1000) / coups }
}

/** Ouvre une descente au premier monstre de la première salle. */
export function entrer(d: Donjon, c: Contenu, rng: () => number): EnDescente | null {
  const restants = composerSalle(d, c, 1, rng)
  const m = restants[0] !== undefined ? monstre(c, restants[0]) : null
  if (!m) return null
  const boss = d.salles === 1
  const pv = pvDeDepart(palierDe(m), boss)
  return {
    donjon: d.id, salle: 1, restants, pvMonstre: pv, pvMonstreMax: pv,
    butin: {}, vaincus: 0, depuisCoup: 0, degatCoup: 0, cadenceMs: INTERVALLE_COUP_MS,
    xp: 0, dureeS: 0, vivres: 0,
  }
}

export interface PasDonjon {
  descente: EnDescente
  /** points de vie du personnage après le pas */
  pv: number
  /** expérience gagnée pendant le pas */
  xp: number
  /** monstres abattus pendant le pas */
  kills: number
  /** null tant que la descente continue */
  fini: "gagne" | "perdu" | null
}

/**
 * Un pas de descente, en temps réel.
 *
 * Le combat se joue à la vie du monstre, pas à un minuteur : c'est ce qui
 * permet de **voir** la barre du boss descendre. Le débit reste celui du farm —
 * la vie de départ divisée par le temps par kill — pour que le plancher de
 * durée d'un combat s'applique ici aussi.
 */
export function pasDonjon(
  s: StatsPerso, d: Donjon, c: Contenu, en: EnDescente, dtS: number,
  rng: () => number, sagesse: number, niveauJoueur: number, pvDepart: number,
): PasDonjon {
  let { salle, restants, pvMonstre, pvMonstreMax, vaincus, depuisCoup, degatCoup, cadenceMs } = en
  const butin = { ...en.butin }
  let pv = pvDepart
  let xp = 0
  let kills = 0
  let reste = dtS

  // La découpe se calcule à l'entrée d'une descente reprise d'une ancienne
  // sauvegarde, où elle n'existait pas encore.
  if (degatCoup <= 0) {
    const id0 = restants[0]
    const m0 = id0 !== undefined ? monstre(c, id0) : null
    if (m0) ({ degatCoup, cadenceMs } = decouper(s, palierDe(m0), salle >= d.salles))
  }

  const sortie = (fini: PasDonjon["fini"], pvFinal = pv) => ({
    descente: {
      ...en, salle, restants, pvMonstre: Math.max(0, pvMonstre), pvMonstreMax, butin, vaincus,
      depuisCoup, degatCoup, cadenceMs,
      xp: en.xp + xp, dureeS: en.dureeS + (dtS - reste), vivres: en.vivres,
    },
    pv: pvFinal, xp, kills, fini,
  })

  // Plusieurs monstres peuvent tomber dans un même pas après une longue absence.
  for (let garde = 0; garde < 400 && reste > 0; garde++) {
    const id = restants[0]
    const m = id !== undefined ? monstre(c, id) : null
    if (!m) return sortie("gagne")
    const pal = palierDe(m)
    const boss = salle >= d.salles
    const encaisse = degatsSubisPs(s, pal) * (boss ? BOSS_DEGATS : 1)

    // On avance jusqu'au prochain coup, ou jusqu'à la fin du pas.
    const jusquauCoup = Math.max(0, (cadenceMs - depuisCoup) / 1000)
    const tranche = Math.min(reste, jusquauCoup)
    pv -= encaisse * tranche
    reste -= tranche
    depuisCoup += tranche * 1000

    if (pv <= 0) return sortie("perdu", 0)
    // Le pas s'arrête avant la frappe suivante : le monstre garde sa vie, elle
    // ne « glisse » pas entre deux coups.
    if (depuisCoup < cadenceMs - 0.001) break

    depuisCoup = 0
    pvMonstre -= degatCoup
    if (pvMonstre > 0.001) continue

    // --- le monstre tombe
    kills++
    vaincus++
    xp += Math.round(xpGagnee(pal.niveau, niveauJoueur) * (1 + sagesse / 100))
    for (const b of m.butin) {
      const taux = (b.taux[pal.palier - 1] ?? b.taux[0] ?? 0) * (1 + s.prospection / 100)
      // Le boss est généreux : ses taux valent le triple d'un garde.
      if (rng() * 100 < taux * (boss ? BOSS_TAUX : 1)) butin[b.item] = (butin[b.item] ?? 0) + 1
    }

    restants = restants.slice(1)
    if (restants.length === 0) {
      if (boss) { pvMonstre = 0; return sortie("gagne") }
      salle++
      restants = composerSalle(d, c, salle, rng)
    }
    const suivant = restants[0] !== undefined ? monstre(c, restants[0]) : null
    if (!suivant) { pvMonstre = 0; return sortie("gagne") }
    const bossSuivant = salle >= d.salles
    pvMonstreMax = pvDeDepart(palierDe(suivant), bossSuivant)
    pvMonstre = pvMonstreMax
    ;({ degatCoup, cadenceMs } = decouper(s, palierDe(suivant), bossSuivant))
  }

  return sortie(null)
}

/**
 * Estimation affichée avant d'entrer : jusqu'où on irait **sans manger**.
 *
 * C'est volontairement pessimiste. Le donjon se tient à coups de vivres, et
 * annoncer une réussite qui suppose un sac plein serait mentir.
 */
export function pronostic(_p: Personnage, s: StatsPerso, d: Donjon, c: Contenu) {
  const rng = () => 0.5
  let en = entrer(d, c, rng)
  if (!en) return { reussi: false, salleAtteinte: 0, dureeS: 0 }
  let pv = s.pvMax
  let duree = 0
  for (let i = 0; i < 400; i++) {
    const pas = pasDonjon(s, d, c, en, 5, rng, 0, 0, pv)
    duree += 5
    en = pas.descente
    pv = pas.pv
    if (pas.fini) return { reussi: pas.fini === "gagne", salleAtteinte: en.salle, dureeS: duree }
  }
  return { reussi: false, salleAtteinte: en.salle, dureeS: duree }
}

export const nomCle = (d: Donjon, c: Contenu) =>
  d.cles.map((k) => item(c, k.item)?.nom ?? "?").join(", ")

export { famille }

/**
 * Combien de descentes les clés en réserve permettent.
 *
 * Sert à proposer une série de la bonne taille : « si on a 50 clés qu'on puisse
 * lancer 50 runs » (Vyshu, 26/08/2026).
 */
export function descentesPossibles(
  d: Donjon, inventaire: Readonly<Record<string, number>>,
): number {
  if (d.cles.length === 0) return 0
  let n = Infinity
  for (const k of d.cles) {
    n = Math.min(n, Math.floor((inventaire[String(k.item)] ?? 0) / k.qte))
  }
  return Number.isFinite(n) ? n : 0
}
